/**
 * Общие правила API-контрактов (ТЗ §12).
 *
 * Все изменяющие операции, которые могут повториться при offline-синхронизации,
 * принимают syncId и дедуплицируются сервером по паре (userId, syncId).
 */

/** Текущая версия API. Меняется только несовместимыми изменениями. */
export const API_VERSION = 'v1';

/** Префикс всех маршрутов. */
export const API_PREFIX = `/${API_VERSION}`;

/**
 * Коды ошибок. Не раскрывают детали реализации и существование email
 * (ТЗ §12, SEC-11).
 */
export const API_ERROR_CODES = [
  'validation_error',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'rate_limited',
  'payload_too_large',
  'internal_error',
  'service_unavailable',
] as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** Единый формат ошибки API. */
export interface ApiError {
  readonly code: ApiErrorCode;
  /** Человекочитаемое сообщение без внутренних деталей. */
  readonly message: string;
  /** Идентификатор запроса для корреляции с логами. */
  readonly requestId: string;
  /** Секунды до повторной попытки; присутствует только при rate_limited. */
  readonly retryAfterSeconds?: number;
}

/** Статусы подписки (ТЗ FR-SUB-08). */
export const SUBSCRIPTION_STATES = [
  'trialing',
  'active',
  'grace_period',
  'past_due',
  'canceled',
  'expired',
  'refunded',
] as const;
export type SubscriptionState = (typeof SUBSCRIPTION_STATES)[number];

/** Возможности, открываемые подпиской. Решение всегда принимает сервер (FR-SUB-10). */
export const ENTITLEMENTS = [
  'unlimited-adaptive-practice',
  'extended-analytics',
  'all-symbol-packs',
  'cloud-sync',
  'custom-goals',
  'personal-programs',
] as const;
export type Entitlement = (typeof ENTITLEMENTS)[number];

/** Снимок доступов пользователя, полученный от сервера. */
export interface EntitlementSnapshot {
  readonly state: SubscriptionState;
  readonly entitlements: readonly Entitlement[];
  /** Календарная метка окончания текущего периода (ISO 8601), если применимо. */
  readonly currentPeriodEndsAt: string | null;
  /** Момент получения снимка (ISO 8601). Используется для ограничения TTL офлайн. */
  readonly issuedAt: string;
}

/** Максимальный возраст кэша entitlement при работе офлайн, мс. */
export const ENTITLEMENT_CACHE_TTL_MS = 72 * 60 * 60 * 1000;
